import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useFreeMaterials } from "../hooks/useFreeMaterials";
import { useNotifications } from "../hooks/useNotifications";
import HamburgerMenu from "../Components/HamburgerMenu/HamburgerMenu";
import HamburgerButton from "../Components/HamburgerMenu/HamburgerButton";
import { ModalMessage } from "../Components/ModalMessage/ModalMessage";
import MaterialCard from "../Components/MaterialCard/MaterialCard";
import Footer from "../Components/Footer/Footer";
import "./MateriaisGratuitosPage.css";

interface Material {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  thumbnailUrl?: string;
}

const MateriaisGratuitosPage: React.FC = () => {
  const { isAuthenticated, logout: authLogout } = useAuth();
  const { hasRecentNotifications } = useNotifications();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const navigate = useNavigate();

  // Hook para materiais gratuitos
  const { fetchFreeMaterials, materialState } = useFreeMaterials();
  const { fetchNotifications } = useNotifications();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    authLogout();
    setShowLogoutModal(false);
  };

  const handleMaterialClick = (material: Material) => {
    navigate(`/material/${material.id}`);
  };

  // Carregar materiais gratuitos e notificações ao montar o componente
  useEffect(() => {
    fetchFreeMaterials();
    fetchNotifications();
  }, [fetchFreeMaterials, fetchNotifications]);

  return (
    <div className="materiais-gratuitos-page">
      <header className="page-header">
        <div className="header-content">
          <div className="header-left">
            {isAuthenticated && (
              <HamburgerButton 
                isOpen={isMenuOpen} 
                onClick={toggleMenu}
                hasNotification={hasRecentNotifications()}
              />
            )}
          </div>
          <div className="header-actions">
            <button
              onClick={
                isAuthenticated ? handleLogout : () => navigate("/login")
              }
              className="auth-button"
            >
              {isAuthenticated ? "Sair" : "Entrar"}
            </button>
          </div>
        </div>
      </header>

      {isAuthenticated && (
        <HamburgerMenu 
          isOpen={isMenuOpen} 
          onClose={closeMenu}
          hasRecentNotifications={hasRecentNotifications()}
        />
      )}

      <main className="page-main">
        <div className="page-title">
          <h1>🎁 Materiais Gratuitos</h1>
        </div>

        {materialState.isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <span>Carregando materiais gratuitos...</span>
          </div>
        ) : materialState.error ? (
          <div className="error-message">
            <h2>❌ Erro ao carregar materiais</h2>
            <p>{materialState.error}</p>
            <button onClick={fetchFreeMaterials} className="retry-button">
              Tentar Novamente
            </button>
          </div>
        ) : materialState.materials.length === 0 ? (
          <div className="no-materials-message">
            <h2>📚 Nenhum material gratuito disponível</h2>
            <p>Ainda não há materiais gratuitos cadastrados. Volte em breve!</p>
          </div>
        ) : (
          <div className="materials-grid">
            {materialState.materials.map((material) => (
              <MaterialCard
                key={material.id}
                id={material.id}
                title={material.title}
                description={material.description}
                thumbnailUrl={material.thumbnailUrl}
                isFree={true}
                onClick={() => handleMaterialClick(material)}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />

      {showLogoutModal && (
        <ModalMessage
          title="Tem certeza?"
          description="Deseja realmente sair da sua conta?"
          leftButtonText="Sim"
          leftButtonAction={confirmLogout}
          rightButtonText="Não"
          rightButtonAction={() => setShowLogoutModal(false)}
        />
      )}
    </div>
  );
};

export default MateriaisGratuitosPage;
